import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, Wrench, Calendar, RefreshCw, Activity } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';

interface BikeMaintenanceData {
  id: string;
  model: string;
  serial_number: string;
  status: string;
  last_service_date: string | null;
  engine_hours: string | null;
  mileage: string | null;
  battery_percentage: number;
  days_since_service: number;
  maintenance_priority: 'low' | 'medium' | 'high' | 'critical';
}

export const BikeMaintenanceAnalytics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [bikes, setBikes] = useState<BikeMaintenanceData[]>([]);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMaintenanceData();
  }, []);

  const fetchMaintenanceData = async () => {
    setLoading(true);
    try {
      const { data: bikesData, error: bikesError } = await supabase
        .from('bikes')
        .select('id, model, serial_number, status, last_service_date, engine_hours, mileage, battery_percentage')
        .order('last_service_date', { ascending: true, nullsFirst: true });

      if (bikesError) throw bikesError;

      const processedData: BikeMaintenanceData[] = (bikesData || []).map(bike => {
        const lastServiceDate = bike.last_service_date ? new Date(bike.last_service_date) : null;
        const daysSinceService = lastServiceDate 
          ? Math.floor((Date.now() - lastServiceDate.getTime()) / (1000 * 60 * 60 * 24))
          : 999;

        // Определяем приоритет обслуживания
        let maintenancePriority: 'low' | 'medium' | 'high' | 'critical' = 'low';
        
        if (!lastServiceDate || daysSinceService > 180) {
          maintenancePriority = 'critical';
        } else if (daysSinceService > 90) {
          maintenancePriority = 'high';
        } else if (daysSinceService > 60) {
          maintenancePriority = 'medium';
        }

        // Учитываем уровень батареи
        if (bike.battery_percentage < 20) {
          maintenancePriority = maintenancePriority === 'critical' ? 'critical' : 'high';
        }

        return {
          id: bike.id,
          model: bike.model,
          serial_number: bike.serial_number || 'Неизвестно',
          status: bike.status,
          last_service_date: bike.last_service_date,
          engine_hours: bike.engine_hours,
          mileage: bike.mileage,
          battery_percentage: bike.battery_percentage || 0,
          days_since_service: daysSinceService,
          maintenance_priority: maintenancePriority
        };
      });

      setBikes(processedData);
    } catch (err: any) {
      console.error("Ошибка при получении данных о техническом состоянии:", err);
      setError(err.message || "Произошла ошибка при загрузке данных");
    } finally {
      setLoading(false);
    }
  };

  const getPriorityBadge = (priority: 'low' | 'medium' | 'high' | 'critical') => {
    switch (priority) {
      case 'critical':
        return <Badge className="bg-red-600 text-white font-semibold">Критический</Badge>;
      case 'high':
        return <Badge className="bg-orange-500 text-white font-semibold">Высокий</Badge>;
      case 'medium':
        return <Badge className="bg-yellow-500 text-white font-semibold">Средний</Badge>;
      default:
        return <Badge className="bg-green-500 text-white font-semibold">Низкий</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'maintenance':
        return <Badge className="bg-red-500 text-white">На обслуживании</Badge>;
      case 'available':
        return <Badge className="bg-green-500 text-white">Доступен</Badge>;
      case 'in_use':
        return <Badge className="bg-blue-500 text-white">В работе</Badge>;
      case 'inactive':
        return <Badge className="bg-gray-500 text-white">Неактивен</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatLastService = (dateString: string | null) => {
    if (!dateString) return 'Не обслуживался';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: ru });
    } catch (e) {
      return 'Некорректная дата';
    }
  };

  // Фильтрация данных
  const filteredBikes = bikes.filter(bike => {
    const statusMatch = filterStatus === 'all' || bike.status === filterStatus;
    const priorityMatch = filterPriority === 'all' || bike.maintenance_priority === filterPriority;
    return statusMatch && priorityMatch;
  });

  // Статистика
  const stats = {
    total: bikes.length,
    critical: bikes.filter(b => b.maintenance_priority === 'critical').length,
    high: bikes.filter(b => b.maintenance_priority === 'high').length,
    needsService: bikes.filter(b => b.days_since_service > 90).length,
    lowBattery: bikes.filter(b => b.battery_percentage < 30).length
  };

  return (
    <div className="space-y-6">
      {/* Статистические карточки */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <div>
                <p className="text-2xl font-bold text-red-600">{stats.critical}</p>
                <p className="text-xs text-muted-foreground">Критический приоритет</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Wrench className="h-4 w-4 text-orange-500" />
              <div>
                <p className="text-2xl font-bold text-orange-600">{stats.needsService}</p>
                <p className="text-xs text-muted-foreground">Требуют обслуживания</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Activity className="h-4 w-4 text-yellow-500" />
              <div>
                <p className="text-2xl font-bold text-yellow-600">{stats.lowBattery}</p>
                <p className="text-xs text-muted-foreground">Низкий заряд батареи</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-blue-500" />
              <div>
                <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Всего велосипедов</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
            <CardTitle className="flex items-center mb-2 sm:mb-0">
              <Wrench className="h-5 w-5 mr-2 text-orange-500" />
              Техническое состояние и обслуживание велосипедов
            </CardTitle>
            <div className="flex gap-2">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Статус" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все статусы</SelectItem>
                  <SelectItem value="available">Доступен</SelectItem>
                  <SelectItem value="in_use">В работе</SelectItem>
                  <SelectItem value="maintenance">На обслуживании</SelectItem>
                  <SelectItem value="inactive">Неактивен</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filterPriority} onValueChange={setFilterPriority}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Приоритет" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все приоритеты</SelectItem>
                  <SelectItem value="critical">Критический</SelectItem>
                  <SelectItem value="high">Высокий</SelectItem>
                  <SelectItem value="medium">Средний</SelectItem>
                  <SelectItem value="low">Низкий</SelectItem>
                </SelectContent>
              </Select>
              
              <Button variant="outline" size="sm" onClick={fetchMaintenanceData}>
                <RefreshCw className="h-4 w-4 mr-1" />
                Обновить
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : error ? (
            <div className="text-center p-4 text-red-500">
              <AlertTriangle className="h-10 w-10 mx-auto mb-2" />
              <p>{error}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Модель</TableHead>
                  <TableHead>Серийный номер</TableHead>
                  <TableHead className="text-center">Статус</TableHead>
                  <TableHead className="text-right">Заряд батареи</TableHead>
                  <TableHead>Последнее ТО</TableHead>
                  <TableHead className="text-right">Пробег</TableHead>
                  <TableHead className="text-center">Приоритет ТО</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBikes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-6 text-muted-foreground">
                      Нет данных для отображения
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredBikes.map((bike) => (
                    <TableRow 
                      key={bike.id} 
                      className={
                        bike.maintenance_priority === 'critical' ? 'bg-red-50 dark:bg-red-950/20' :
                        bike.maintenance_priority === 'high' ? 'bg-orange-50 dark:bg-orange-950/20' :
                        bike.maintenance_priority === 'medium' ? 'bg-yellow-50 dark:bg-yellow-950/20' : 
                        ''
                      }
                    >
                      <TableCell className="font-medium">{bike.model}</TableCell>
                      <TableCell>{bike.serial_number}</TableCell>
                      <TableCell className="text-center">
                        {getStatusBadge(bike.status)}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={
                          bike.battery_percentage < 20 ? 'text-red-600 font-bold' :
                          bike.battery_percentage < 50 ? 'text-orange-600 font-semibold' :
                          'text-green-600'
                        }>
                          {bike.battery_percentage}%
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1 text-muted-foreground" />
                          {formatLastService(bike.last_service_date)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {bike.mileage || 'Не указан'}
                      </TableCell>
                      <TableCell className="text-center">
                        {getPriorityBadge(bike.maintenance_priority)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}

          <div className="mt-4 text-sm text-muted-foreground">
            <p><strong>Критерии приоритета обслуживания:</strong></p>
            <p>• <span className="text-red-600 font-semibold">Критический</span>: более 180 дней без ТО или никогда не обслуживался</p>
            <p>• <span className="text-orange-600 font-semibold">Высокий</span>: более 90 дней без ТО или заряд батареи менее 20%</p>
            <p>• <span className="text-yellow-600 font-semibold">Средний</span>: более 60 дней без ТО</p>
            <p>• <span className="text-green-600 font-semibold">Низкий</span>: менее 60 дней без ТО</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
